import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronLeft,
    faChevronRight,
    faEye,
    faFileCsv,
    faFileExcel,
    faFilePdf,
    faFilter,
} from "@fortawesome/free-solid-svg-icons";
import { Card, Table, Button, Form, Badge } from "@themesberg/react-bootstrap";
import { adminAllCourseList, userGetReport, userExportReport } from "../../../pages/api";

const ACTIVE_BUTTON_STYLE = {
    background: "#1D4E89",
    border: "none",
    borderRadius: "0.5rem",
    color: "#ffffff",
};

const DEFAULT_FILTERS = {
    // course: "",
    // monthYear: "",
    status: "",
    level: "",
};

const levelOptions = [
    { value: "", label: "All Levels" },
    ...Array.from({ length: 9 }, (_, i) => ({
        value: String(i + 1),
        label: `Level ${i + 1}`,
    })),
];


const formatDate = (value) => {
    if (!value) return "N/A" || "-";
    const normalized = String(value).includes(" ") ? String(value).replace(" ", "T") : value;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).format(date);
};

const formatAmount = (value) => {
    if (value === null || value === undefined || value === "") return "N/A" || "-";
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);

    return new Intl.NumberFormat("en-IN", {
        maximumFractionDigits: 2,
    }).format(num);
};

const formatLabel = (value) =>
    String(value || "N/A" || "-")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());

const getStatusBadgeStyle = (status) => {
    switch (String(status || "").toLowerCase()) {
        case "active":
        case "success":
        case "completed":
        case "generated":
        case "approved":
        case "processed":
        case "verified":
            return { background: "#10b981", color: "#ffffff" };
        case "pending":
        case "Pending":
        case "estimated":
            return { background: "#f59e0b", color: "#ffffff" };
        case "inactive":
        case "blocked":
        case "failed":
        case "rejected":
            return { background: "#ef4444", color: "#ffffff" };
        default:
            return { background: "#6b7280", color: "#ffffff" };
    }
};

export default function UserReports() {
    const [loading, setLoading] = useState(false);
    const [courseLoading, setCourseLoading] = useState(false);
    const [showReport, setShowReport] = useState(false);
    const [courses, setCourses] = useState([]);
    const [reports, setReports] = useState([]);
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
    });

    const monthInputRef = useRef(null);

    const courseOptions = useMemo(
        () =>
            courses.map((course) => ({
                value: String(course.id),
                label: course.title || course.name || `Course #${course.id}`,
            })),
        [courses]
    );

    const openMonthPicker = (e) => {
        const input = e.currentTarget;
        if (typeof input.showPicker === "function") {
            try {
                input.showPicker();
            } catch (error) {
                // native fallback
            }
        }
    };

    // const fetchCourses = async () => {
    //     try {
    //         setCourseLoading(true);
    //         const response = await adminAllCourseList({ pagination: false });
    //         const responseData = response?.data || {};
    //         const apiData = responseData?.data || {};
    //         const apiCourses = Array.isArray(apiData?.courses)
    //             ? apiData.courses
    //             : Array.isArray(apiData)
    //                 ? apiData
    //                 : [];
    //         setCourses(apiCourses);
    //     } catch (error) {
    //         console.error("Failed to fetch courses:", error);
    //         toast.error(error?.response?.data?.message || "Unable to load courses.");
    //         setCourses([]);
    //     } finally {
    //         setCourseLoading(false);
    //     }
    // };

    const buildParams = (customFilters = filters, page = 1, perPage = 10) => {
        const params = { page, per_page: perPage };

        // if (customFilters.course) params.course_id = customFilters.course;
        // if (customFilters.monthYear) params.month_year = customFilters.monthYear;
        if (customFilters.status) params.status = customFilters.status;
        if (customFilters.level) params.level = customFilters.level;

        return params;
    };

    const fetchReports = async (customFilters = filters, page = 1, perPage = pagination.per_page || 10) => {
        try {
            setLoading(true);

            const response = await userGetReport(buildParams(customFilters, page, perPage));
            const responseData = response?.data || {};

            const apiPayload = responseData?.data;
            const apiReports = Array.isArray(apiPayload)
                ? apiPayload
                : Array.isArray(apiPayload?.reports)
                    ? apiPayload.reports
                    : [];

            const apiPagination = responseData?.pagination || apiPayload?.pagination || {};

            setReports(apiReports);
            setPagination({
                current_page: Number(apiPagination.current_page ?? page ?? 1),
                last_page: Number(apiPagination.last_page ?? 1),
                per_page: Number(apiPagination.per_page ?? perPage ?? 10),
                total: Number(apiPagination.total ?? apiReports.length),
            });

            setShowReport(true);
        } catch (error) {
            // console.error("Failed to fetch level network report:", error);
            setReports([]);
            setPagination({
                current_page: 1,
                last_page: 1,
                per_page: 10,
                total: 0,
            });
            setShowReport(true);
            toast.error(error?.response?.data?.message || "Unable to load report.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // fetchCourses();
        fetchReports(DEFAULT_FILTERS, 1, 10);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFilterChange = (key, value) => {
        const nextFilters = {
            ...filters,
            [key]: value,
        };

        setFilters(nextFilters);
        setPagination((prev) => ({ ...prev, current_page: 1 }));
        fetchReports(nextFilters, 1, pagination.per_page || 10);
    };

    const handleViewReport = () => {
        fetchReports(filters, 1, pagination.per_page || 10);
    };

    const buildExportParams = () => {
        const params = {};

        // if (filters.course) params.course_id = filters.course;
        // if (filters.monthYear) params.month_year = filters.monthYear;
        if (filters.status) params.status = filters.status;
        if (pagination.per_page) params.per_page = pagination.per_page;

        return params;
    };

    const downloadBlobFile = (blob, fileName) => {
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
    };

    const handleExport = async (format) => {
        try {
            const params = buildExportParams();
            params.format = format;

            const response = await userExportReport(params);
            const blob = response?.data;

            if (!blob) {
                toast.error("Export file not received.");
                return;
            }

            const fileExt = format === "pdf" ? "pdf" : format === "csv" ? "csv" : "xlsx";
            downloadBlobFile(blob, `level-network-report.${fileExt}`);
            toast.success("Report exported successfully.");
        } catch (error) {
            console.error("Export failed:", error);
            toast.error(error?.response?.data?.message || "Unable to export report.");
        }
    };

    const columns = [
        "Sr No",
        "Customer Id",
        "Referral Number",
        "User Name",
        "Level",
        "Referred By",
        "Earnings",
        "KYC Status",
        "Status",
    ];


    const renderCell = (item, column) => {
        switch (column) {
            case "Customer Id":
                return item.custom_user_id ?? "N/A";
            case "Referral Number":
                return item.referral_number ?? "N/A";
            case "User Name":
                return item.user_name ?? "N/A";
            case "Level":
                return item.level ?? "N/A";
            case "Referred By":
                return item.referred_by ?? "N/A";
            case "Earnings":
                return item.earnings ?? "N/A";
            case "KYC Status":
                // return item.kyc_status ?? "N/A";
                return (
                    <Badge
                        className="rounded-pill d-inline-flex align-items-center justify-content-center"
                        style={{
                            ...getStatusBadgeStyle(item.kyc_status),
                            minWidth: "5.5rem",
                            height: "2rem",
                            padding: "0 12px",
                        }}
                    >
                        {formatLabel(item.kyc_status) ?? "N/A"}
                    </Badge>
                );
            case "Status":
                return (
                    <Badge
                        className="rounded-pill d-inline-flex align-items-center justify-content-center"
                        style={{
                            ...getStatusBadgeStyle(item.status),
                            minWidth: "5.5rem",
                            height: "2rem",
                            padding: "0 12px",
                        }}
                    >
                        {formatLabel(item.status) ?? "N/A"}
                    </Badge>
                );
            default:
                return "N/A" || "-";
        }
    };

    const totalPages = pagination.last_page || 1;
    const currentPage = pagination.current_page || 1;
    const startIndex = (currentPage - 1) * pagination.per_page;

    const getShowingRange = () => {
        const total = pagination.total || 0;
        const current = pagination.current_page || 1;
        const perPage = pagination.per_page || 10;

        if (total === 0) return { start: 0, end: 0 };

        const start = (current - 1) * perPage + 1;
        const end = Math.min(current * perPage, total);
        return { start, end };
    };

    const getPageItems = () => {
        if (totalPages <= 5) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        if (currentPage <= 2) {
            return [1, 2, 3, "...", totalPages];
        }

        if (currentPage >= totalPages - 1) {
            return [1, "...", totalPages - 2, totalPages - 1, totalPages];
        }

        return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
    };

    const { start, end } = getShowingRange();

    return (
        <>
            <div className="user-reports-page">
                <div className="user-reports-header py-4">
                    <div className="user-reports-heading-wrap">
                        <h2 className="h4 mb-1 user-reports-title">User Report</h2>
                    </div>
                </div>

                <Card className="border-0 shadow-sm rounded-4 mb-4">
                    <Card.Body className="p-4">
                        <div className="user-reports-filter-grid">
                            {/* <div>
                                <Form.Label className="fw-semibold">Course Name</Form.Label>
                                <Form.Select
                                    value={filters.course}
                                    onChange={(e) => handleFilterChange("course", e.target.value)}
                                    disabled={courseLoading}
                                >
                                    <option value="">{courseLoading ? "Loading courses..." : "All Courses"}</option>
                                    {courseOptions.map((item) => (
                                        <option key={item.value} value={item.value}>
                                            {item.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </div> */}

                            <div>
                                <Form.Label className="fw-semibold">Level</Form.Label>
                                <Form.Select
                                    value={filters.level}
                                    onChange={(e) => handleFilterChange("level", e.target.value)}
                                >
                                    {levelOptions.map((item) => (
                                        <option key={item.value || "all-levels"} value={item.value}>
                                            {item.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </div>

                            <div>
                                <Form.Label className="fw-semibold">Status</Form.Label>
                                <Form.Select
                                    value={filters.status}
                                    onChange={(e) => handleFilterChange("status", e.target.value)}
                                >
                                    <option value="">All Status</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="blocked">Blocked</option>
                                </Form.Select>
                            </div>

                            {/* <div>
                                <Form.Label className="fw-semibold">Month / Year</Form.Label>
                                <Form.Control
                                    ref={monthInputRef}
                                    type="month"
                                    value={filters.monthYear}
                                    onChange={(e) => handleFilterChange("monthYear", e.target.value)}
                                    onMouseDown={openMonthPicker}
                                    onClick={openMonthPicker}
                                    className="network-month-filter"
                                    style={{ boxShadow: "none" }}
                                />
                            </div> */}
                        </div>

                        <div className="user-reports-action-row mt-4">
                            {/* <Button
                                type="button"
                                style={ACTIVE_BUTTON_STYLE}
                                className="fw-semibold user-view-report-btn"
                                onClick={handleViewReport}
                            >
                                <FontAwesomeIcon icon={faEye} className="me-2" />
                                View Report
                            </Button> */}

                            <div className="user-reports-export-group">
                                <Button
                                    type="button"
                                    variant="outline-primary"
                                    className="fw-semibold"
                                    onClick={() => handleExport("csv")}
                                >
                                    <FontAwesomeIcon icon={faFileCsv} />
                                    {/* CSV */}
                                </Button>

                                <Button
                                    type="button"
                                    variant="outline-primary"
                                    className="fw-semibold"
                                    onClick={() => handleExport("excel")}
                                >
                                    <FontAwesomeIcon icon={faFileExcel} />
                                    {/* Excel */}
                                </Button>

                                <Button
                                    type="button"
                                    variant="outline-primary"
                                    className="fw-semibold"
                                    onClick={() => handleExport("pdf")}
                                >
                                    <FontAwesomeIcon icon={faFilePdf} />
                                    {/* PDF */}
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                {showReport ? (
                    <Card className="border-0 shadow-sm rounded-4">
                        <Card.Body className="p-4">
                            <div className="user-reports-table-top d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                <div className="user-reports-list-title-wrap">
                                    <h5 className="mb-1">Level Network Report</h5>
                                    {/* <div className="text-muted small">
                                        Filtered results for selected course, month, and status
                                    </div> */}
                                </div>
                            </div>

                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 user-reports-table">
                                    <thead>
                                        <tr>
                                            {columns.map((col) => (
                                                <th key={col}>{col}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan={columns.length} className="text-center text-muted py-5">
                                                    Loading report data...
                                                </td>
                                            </tr>
                                        ) : reports.length > 0 ? (
                                            reports.map((item, index) => (
                                                <tr key={item.id ?? `${item.level ?? "level"}-${index}`}>
                                                    {columns.map((col) => (
                                                        <td key={`${item.id ?? index}-${col}`}>
                                                            {col === "Sr No" ? startIndex + index + 1 : renderCell(item, col)}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={columns.length} className="text-center text-muted py-5">
                                                    No records found for selected filters.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                                <div className="text-muted small">
                                    Showing {start} to {end} of {pagination.total} entries
                                </div>

                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <Button
                                        type="button"
                                        variant="outline-primary"
                                        disabled={pagination.current_page <= 1 || loading}
                                        onClick={() => {
                                            fetchReports(filters, Math.max(1, pagination.current_page - 1), pagination.per_page || 10);
                                        }}
                                        className="course-page-btn"
                                    >
                                        <FontAwesomeIcon icon={faChevronLeft} />
                                    </Button>

                                    {getPageItems().map((item, index) =>
                                        item === "..." ? (
                                            <span key={`dots-${index}`} className="text-muted px-1">
                                                ...
                                            </span>
                                        ) : (
                                            <Button
                                                key={item}
                                                type="button"
                                                variant="outline-primary"
                                                disabled={loading}
                                                onClick={() => {
                                                    fetchReports(filters, item, pagination.per_page || 10);
                                                }}
                                                className={`course-page-btn ${item === pagination.current_page ? "course-page-btn-active" : ""
                                                    }`}
                                            >
                                                {item}
                                            </Button>
                                        )
                                    )}

                                    <Button
                                        type="button"
                                        variant="outline-primary"
                                        disabled={pagination.current_page >= pagination.last_page || loading}
                                        onClick={() => {
                                            fetchReports(
                                                filters,
                                                Math.min(pagination.last_page, pagination.current_page + 1),
                                                pagination.per_page || 10
                                            );
                                        }}
                                        className="course-page-btn"
                                    >
                                        <FontAwesomeIcon icon={faChevronRight} />
                                    </Button>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                ) : (
                    <Card className="border-0 shadow-sm rounded-4">
                        <Card.Body className="p-5 text-center text-muted">
                            <FontAwesomeIcon icon={faFilter} className="mb-3" style={{ fontSize: "1.5rem" }} />
                            <div className="fw-semibold">Loading report...</div>
                        </Card.Body>
                    </Card>
                )}
            </div>

            <style>{`
        .user-reports-page {
          width: 100%;
          padding-inline: 0;
        }

        .user-reports-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: nowrap;
        }

        .user-reports-heading-wrap {
          min-width: 0;
          flex: 0 0 auto;
        }

        .user-reports-title {
          font-size: clamp(1.4rem, 2vw, 2rem);
          line-height: 1.2;
          word-break: break-word;
          white-space: nowrap;
        }

        .user-reports-filter-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 1rem;
        }

        .user-reports-action-row {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .user-view-report-btn {
          white-space: nowrap;
          flex: 0 0 auto;
        }

        .user-reports-export-group {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          white-space: nowrap;
        }

        .user-reports-export-group .btn {
          white-space: nowrap;
          flex: 0 0 auto;
        }

        .user-reports-table-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .user-reports-list-title-wrap {
          min-width: 0;
          flex: 1 1 auto;
        }

        @media (max-width: 991.98px) {
          .user-reports-filter-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }

        @media (max-width: 767.98px) {
          .user-reports-header {
            flex-wrap: wrap;
            align-items: flex-start;
            gap: 0.85rem;
          }

          .user-reports-filter-grid {
            grid-template-columns: 1fr;
          }

          .user-reports-action-row {
            justify-content: flex-end;
          }

          .user-view-report-btn {
            margin-left: auto;
          }
        }

        @media (max-width: 575.98px) {
          .user-reports-title {
            font-size: 1.35rem;
          }

          .user-reports-action-row {
            flex-direction: column;
            align-items: flex-end;
          }

          .user-view-report-btn {
            width: auto;
            align-self: flex-end;
          }

          .user-reports-export-group {
            width: 100%;
            justify-content: flex-end;
          }
        }
      `}</style>
        </>
    );
}